import { Feeders, TimbaPrediction } from '../api/client';
import { teamCode } from './flags';

// Texto corto del pronóstico cargado: "2–1" o, si fue empate, "2–2 (BRA)" con la abreviatura de la
// selección elegida para la definición (alargue o penales). Sin equipos, cae a "(1º)"/"(2º)".
export function formatPick(p: TimbaPrediction, t1?: string | null, t2?: string | null): string {
  const base = `${p.g1}–${p.g2}`;
  if (p.g1 === p.g2 && p.pen) {
    const who = p.pen === 1 ? t1 : t2;
    const label = who ? teamCode(who) : p.pen === 1 ? '1º' : '2º';
    return `${base} (${label})`;
  }
  return base;
}

// Lado que el jugador hizo avanzar (1|2) según su pronóstico; null si empate sin definir.
export function predAdvancer(p: TimbaPrediction): number | null {
  if (p.g1 > p.g2) return 1;
  if (p.g2 > p.g1) return 2;
  return p.pen === 1 || p.pen === 2 ? p.pen : null;
}

export interface Mult {
  booster: number; // x1/x2/x3/x4 — realce TODAVÍA alcanzable (respeta los resultados ya jugados)
  bonus: number; // +1 por equipo clavado en el cruce (los dos = +2)
}

// Booster + bonus alcanzable de cada partido: el realce máximo que TODAVÍA se puede conseguir según
// los pronósticos del jugador y los resultados ya conocidos. Espejo de timba_multipliers del backend
// (ver timba_feeder_hit), con un matiz para los alimentadores aún NO jugados:
//   - alimentador ya jugado  → "acierto" solo si tu pick == quien avanzó de verdad (si no, MUERTO).
//   - alimentador sin jugar  → optimista: "acierto" si lo pronosticaste (todavía podés clavarlo).
// Sin `results`, todos los cruces se tratan como "sin jugar" → potencial optimista (comportamiento
// histórico). Devuelve solo los partidos con algún realce (booster>1 o bonus>0).
export function potentialMultipliers(
  preds: Map<number, TimbaPrediction>,
  feeders?: Feeders,
  results?: Map<number, 1 | 2>, // num → lado que avanzó de verdad (solo cruces ya definidos)
): Map<number, Mult> {
  const tree: Feeders = feeders ?? {};
  const has = (num: number): boolean => {
    const p = preds.get(num);
    return !!p && predAdvancer(p) !== null;
  };
  // ¿El alimentador `num` sigue (o quedó) acertado? Ya jugado: solo si tu pick == realidad.
  const hit = (num: number): boolean => {
    const real = results?.get(num);
    if (real != null) {
      const p = preds.get(num);
      return !!p && predAdvancer(p) === real;
    }
    return has(num);
  };
  const cache = new Map<number, number>();
  const boosterOf = (num: number): number => {
    const c = cache.get(num);
    if (c !== undefined) return c;
    const f = tree[num];
    if (!f) {
      cache.set(num, 1);
      return 1;
    }
    const na = parseInt(f[0].slice(1), 10);
    const nb = parseInt(f[1].slice(1), 10);
    const b = hit(na) && hit(nb) ? Math.min(boosterOf(na), boosterOf(nb)) + 1 : 1;
    cache.set(num, b);
    return b;
  };

  const out = new Map<number, Mult>();
  for (const key of Object.keys(tree)) {
    const num = Number(key);
    const [ea, eb] = tree[num];
    const hits = (hit(parseInt(ea.slice(1), 10)) ? 1 : 0) + (hit(parseInt(eb.slice(1), 10)) ? 1 : 0);
    const booster = boosterOf(num);
    if (booster > 1 || hits > 0) out.set(num, { booster, bonus: hits });
  }
  return out;
}
